import { getApiBaseUrl, getLoginToken } from './user';

export type PracticeRecordStatus = 'locked' | 'ready' | 'active' | 'done';
export type PracticeRecordOrigin = 'self_created' | 'shared_collected';
export type PracticeShareState = 'private' | 'shared';

export type PracticeRecordSummary = {
  recordId: string;
  branchId: string;
  templateId: string | null;
  title: string | null;
  note: string | null;
  sourceGameId: string;
  sourceRoomType: string | null;
  sourceSeat: number;
  scene: string;
  status: PracticeRecordStatus;
  recordOrigin: PracticeRecordOrigin;
  shareState: PracticeShareState;
  practiceGameId: string | null;
  lastRunFinishedAt: number | null;
  templateStatus: 'active' | 'disabled' | null;
  createdAt: number;
  updatedAt: number;
};

type ApiMethod = 'GET' | 'POST' | 'PATCH';

async function apiJson(path: string, params: { method: ApiMethod; body?: any }): Promise<any> {
  const token = getLoginToken();
  if (!token) {
    throw new Error('missing login token');
  }
  const resp = await fetch(`${getApiBaseUrl()}${path.startsWith('/') ? '' : '/'}${path}`, {
    method: params.method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: params.body !== undefined ? JSON.stringify(params.body) : undefined,
  });
  const text = await resp.text();
  let json: any = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!resp.ok) {
    const error = new Error(String(json?.error ?? `${resp.status} ${resp.statusText}`)) as Error & { status?: number; data?: any };
    error.status = resp.status;
    error.data = json;
    throw error;
  }
  return json;
}

export async function listPracticeRecords(): Promise<{ mine: Array<PracticeRecordSummary>; shared: Array<PracticeRecordSummary> }> {
  return apiJson('/me/practice-records', { method: 'GET' });
}

export async function enterPracticeRecord(recordId: string): Promise<{ record: PracticeRecordSummary; gameId: string; action: string }> {
  const id = String(recordId ?? '').trim();
  if (!id) throw new Error('missing recordId');
  return apiJson(`/practice-records/${encodeURIComponent(id)}/enter`, { method: 'POST' });
}

export async function deletePracticeRecord(recordId: string): Promise<{ ok: true; recordId: string }> {
  const id = String(recordId ?? '').trim();
  if (!id) throw new Error('missing recordId');
  return apiJson(`/practice-records/${encodeURIComponent(id)}/delete`, { method: 'POST' });
}

export async function sharePracticeRecord(recordId: string): Promise<{
  record: PracticeRecordSummary;
  templateId: string;
  shareId: string;
  sharePath: string;
}> {
  const id = String(recordId ?? '').trim();
  if (!id) throw new Error('missing recordId');
  return apiJson(`/practice-records/${encodeURIComponent(id)}/share`, { method: 'POST' });
}

export async function patchPracticeRecord(recordId: string, params: { title?: string | null; note?: string | null }): Promise<{ record: PracticeRecordSummary }> {
  const id = String(recordId ?? '').trim();
  if (!id) throw new Error('missing recordId');
  return apiJson(`/practice-records/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: {
      title: params.title ?? null,
      note: params.note ?? null,
    },
  });
}

export async function openPracticeShare(shareId: string): Promise<{ record: PracticeRecordSummary; gameId: string; action: string }> {
  const id = String(shareId ?? '').trim();
  if (!id) throw new Error('missing shareId');
  return apiJson(`/practice-shares/${encodeURIComponent(id)}/open`, { method: 'POST' });
}
